<template>
    <el-dialog
        :visible="visible"
        title="Gán người duyệt CV"
        width="540px"
        custom-class="ro-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <div v-if="order" class="ro-assign">
            <div class="ro-assign__order">
                <div class="ro-assign__code">#{{ order.id }}</div>
                <div class="ro-assign__title">
                    {{ order.title || order.position_title || "Yêu cầu tuyển dụng" }}
                    <span v-if="order.quantity">· SL: {{ order.quantity }}</span>
                </div>
                <div class="ro-assign__sub">
                    <span v-if="departmentName">Phòng ban: <b>{{ departmentName }}</b> · </span>
                    <span>Người tạo: <b>{{ creatorName }}</b></span>
                </div>
                <div class="ro-assign__current">
                    <span class="ro-assign__current-label">Người duyệt CV hiện tại:</span>
                    <span v-if="currentCvApproverName" class="ro-assign__current-val">
                        <i class="el-icon-check"></i> {{ currentCvApproverName }}
                    </span>
                    <span v-else class="ro-assign__current-empty">Chưa phân công</span>
                </div>
            </div>

            <el-form label-position="top" size="small" class="ro-assign__form">
                <el-form-item label="Chọn nhân sự duyệt CV">
                    <el-select
                        v-model="selectedUserId"
                        filterable
                        remote
                        clearable
                        :remote-method="remoteSearch"
                        :loading="searching"
                        placeholder="Nhập tên, email hoặc SĐT để tìm nhân sự..."
                        class="w-100"
                    >
                        <el-option
                            v-for="u in userOptions"
                            :key="u.id"
                            :label="userOptionLabel(u)"
                            :value="u.id"
                        >
                            <div class="ro-user-opt">
                                <span class="ro-user-opt__name">{{ u.name }}</span>
                                <span v-if="u.department_name || u.team_name || u.email" class="ro-user-opt__sub">
                                    {{ u.department_name || u.team_name || u.email }}
                                </span>
                            </div>
                        </el-option>
                    </el-select>
                    <div class="ro-assign__help">
                        <i class="el-icon-info"></i>
                        Nhân sự được gán sẽ là người chịu trách nhiệm duyệt hồ sơ CV của các ứng viên ứng tuyển vào yêu cầu này.
                    </div>
                </el-form-item>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" @click="close">Huỷ</el-button>
            <el-button
                size="small"
                :type="selectedUserId ? 'primary' : 'warning'"
                :loading="submitting"
                @click="submit"
            >
                {{ selectedUserId ? "Xác nhận gán" : "Lưu (Bỏ gán)" }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import { RO_GET_TEACHER_GROUPS } from "@/core/services/store/recruitment-orders/recruitmentOrders.module";
import {
    isOrderRejected,
    COORDINATOR_USER_ID,
    COORDINATOR_NAME,
} from "@/core/config/recruitmentOrders/recruitmentOrdersOption";

export default {
    name: "AssignCvApproverDialog",
    props: {
        visible: { type: Boolean, default: false },
        order: { type: Object, default: null },
        submitting: { type: Boolean, default: false },
    },
    data() {
        return {
            selectedUserId: null,
            userOptions: [],
            searching: false,
            debounceTimer: null,
        };
    },
    computed: {
        departmentName() {
            if (!this.order) return "";
            if (this.order.department && this.order.department.name) return this.order.department.name;
            return this.order.department_name || "";
        },
        creatorName() {
            if (!this.order) return "—";
            if (this.order.created_user_info && this.order.created_user_info.name) return this.order.created_user_info.name;
            return this.order.created_by_name || (this.order.created_user ? `ID ${this.order.created_user}` : "—");
        },
        currentCvApproverName() {
            if (!this.order) return "";
            if (this.order.cv_approver_id == null && !this.order.cv_approver) return "";
            if (this.order.cv_approver && this.order.cv_approver.name) {
                if (!this.order.cv_approver_id || String(this.order.cv_approver.id) === String(this.order.cv_approver_id)) {
                    return this.order.cv_approver.name;
                }
            }
            if (this.order.cv_approver_name) return this.order.cv_approver_name;
            if (this.order.cv_approver_id) {
                if (String(this.order.cv_approver_id) === String(COORDINATOR_USER_ID)) {
                    return "Ms. Cẩm Tú";
                }
                return `ID #${this.order.cv_approver_id}`;
            }
            return "";
        },
    },
    watch: {
        visible(val) {
            if (val && this.order) {
                this.initData();
            }
        },
    },
    methods: {
        initData() {
            const currentId = this.order.cv_approver_id || (this.order.cv_approver && this.order.cv_approver.id) || null;
            this.selectedUserId = currentId ? Number(currentId) : null;

            const initialList = [];
            if (this.order.cv_approver && this.order.cv_approver.id) {
                initialList.push(this.order.cv_approver);
            } else if (currentId && String(currentId) === String(COORDINATOR_USER_ID)) {
                initialList.push({ id: COORDINATOR_USER_ID, name: "Ms. Cẩm Tú" });
            }
            this.userOptions = initialList;

            // Tải danh sách từ API job-recruitment/teacher-groups
            this.fetchUsers("");
        },
        userOptionLabel(u) {
            if (!u) return "";
            const extra = u.department_name || u.team_name || u.email;
            return extra ? `${u.name} (${extra})` : u.name;
        },
        remoteSearch(query) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.fetchUsers(query);
            }, 300);
        },
        fetchUsers(keyword) {
            this.searching = true;
            this.$store
                .dispatch(RO_GET_TEACHER_GROUPS, { keyword })
                .then((list) => {
                    const results = Array.isArray(list) ? list : [];
                    if (this.selectedUserId) {
                        const existing = this.userOptions.find((u) => String(u.id) === String(this.selectedUserId));
                        if (existing && !results.some((u) => String(u.id) === String(this.selectedUserId))) {
                            results.unshift(existing);
                        }
                    }
                    this.userOptions = results;
                })
                .catch(() => {})
                .finally(() => {
                    this.searching = false;
                });
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            clearTimeout(this.debounceTimer);
            this.selectedUserId = null;
            this.userOptions = [];
        },
        submit() {
            if (!this.order) return;
            if (isOrderRejected(this.order)) {
                this.$message.warning("Yêu cầu tuyển dụng đã bị từ chối, không thể gán người duyệt CV");
                return;
            }
            const selectedObj = this.userOptions.find((u) => String(u.id) === String(this.selectedUserId)) || null;
            this.$emit("submit", {
                id: this.order.id,
                cv_approver_id: this.selectedUserId || null,
                cv_approver: selectedObj,
            });
        },
    },
};
</script>

<style scoped>
.ro-assign {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ro-assign__order {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 16px;
}
.ro-assign__code {
    font-size: 11px;
    font-weight: 700;
    color: #6366f1;
    margin-bottom: 2px;
}
.ro-assign__title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.4;
}
.ro-assign__sub {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
}
.ro-assign__current {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e2e8f0;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
}
.ro-assign__current-label {
    color: #64748b;
}
.ro-assign__current-val {
    font-weight: 600;
    color: #059669;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.ro-assign__current-empty {
    color: #94a3b8;
    font-style: italic;
}
.ro-assign__form {
    margin-top: 4px;
}
.ro-assign__help {
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
    margin-top: 6px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
}
.ro-assign__help i {
    color: #6366f1;
    margin-top: 2px;
}
.ro-user-opt {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}
.ro-user-opt__name {
    font-weight: 500;
}
.ro-user-opt__sub {
    font-size: 12px;
    color: #94a3b8;
    margin-left: 12px;
}
</style>
